import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const INSTAGRAM_URL = "https://www.instagram.com/atelier_ritual/";
const HANDLE = "@atelier_ritual";
const TOKEN = import.meta.env.VITE_INSTAGRAM_ACCESS_TOKEN as string | undefined;
const API_URL = TOKEN
  ? `https://graph.instagram.com/v21.0/me/media?fields=id,caption,media_url,permalink,thumbnail_url,media_type,timestamp&limit=6&access_token=${TOKEN}`
  : null;

interface Post {
  id: string;
  caption?: string;
  media_url: string;
  thumbnail_url?: string;
  permalink: string;
  media_type: "IMAGE" | "VIDEO" | "CAROUSEL_ALBUM";
}

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.55, ease: "easeOut" as const },
  },
};

/* ─── Skeleton tile ─────────────────────────────────────────────────── */
const SkeletonTile = () => (
  <div className="aspect-square bg-primary-foreground/8 animate-pulse" />
);

/* ─── Post tile ─────────────────────────────────────────────────────── */
const PostTile = ({ post }: { post: Post }) => {
  const imageSrc =
    post.media_type === "VIDEO" ? post.thumbnail_url ?? post.media_url : post.media_url;

  // Trim caption to a short quote for the hover overlay
  const caption = post.caption
    ? post.caption.replace(/#\S+/g, "").trim().split(/\n/)[0].slice(0, 60)
    : "";

  return (
    <motion.a
      variants={itemVariants}
      href={post.permalink}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block overflow-hidden aspect-square bg-primary-foreground/5"
      aria-label="View post on Instagram"
    >
      <img
        src={imageSrc}
        alt={caption || "Ritual atelier post"}
        className="w-full h-full object-cover transition-transform duration-700 ease-smooth group-hover:scale-105"
        loading="lazy"
      />

      {/* VIDEO badge */}
      {post.media_type === "VIDEO" && (
        <div className="absolute top-2.5 right-2.5 bg-foreground/60 p-1.5">
          <svg viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3 text-primary-foreground/80">
            <path d="M6 3.5l7 4.5-7 4.5V3.5z" />
          </svg>
        </div>
      )}

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/60 transition-all duration-400 flex flex-col items-center justify-center gap-2.5 px-4">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.25"
          className="w-7 h-7 text-primary-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          aria-hidden="true"
        >
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
        </svg>

        {caption && (
          <p className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-75 font-display text-sm italic text-primary-foreground/90 text-center leading-snug">
            {caption}
          </p>
        )}
      </div>
    </motion.a>
  );
};

/* ─── Main section ──────────────────────────────────────────────────── */
const InstagramSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-80px" });

  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!API_URL) {
      setLoading(false);
      setError(true);
      return;
    }

    fetch(API_URL)
      .then((res) => {
        if (!res.ok) throw new Error("API error");
        return res.json();
      })
      .then((json) => {
        setPosts((json.data as Post[]) ?? []);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  return (
    <section
      ref={sectionRef}
      className="w-full bg-foreground text-primary-foreground py-20 px-6 overflow-hidden"
    >
      {/* ── Header ── */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.65, ease: [0.4, 0, 0.2, 1] }}
        className="max-w-6xl mx-auto mb-14"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          {/* Left */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.25"
                className="w-5 h-5 text-gold shrink-0"
                aria-hidden="true"
              >
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
              </svg>
              <span className="font-body text-[9px] tracking-[0.4em] uppercase text-gold">
                Instagram
              </span>
            </div>

            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-primary-foreground leading-none">
              Follow the&nbsp;<em className="not-italic text-gold">Ritual</em>
            </h2>

            <p className="mt-3 font-body text-sm text-primary-foreground/45 max-w-xs leading-relaxed">
              Behind-the-scenes moments, fabric studies, and finished pieces — all made with intention.
            </p>
          </div>

          {/* Right */}
          <div className="flex flex-col items-start md:items-end gap-4">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-body text-sm tracking-widest text-primary-foreground/50 hover:text-gold transition-colors duration-300"
            >
              {HANDLE}
            </a>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 border border-primary-foreground/20 hover:border-gold px-6 py-3 transition-all duration-400"
            >
              <span className="font-body text-[10px] tracking-[0.3em] uppercase text-primary-foreground/60 group-hover:text-gold transition-colors duration-300">
                Follow us
              </span>
              <span className="block h-px w-4 bg-primary-foreground/30 group-hover:w-8 group-hover:bg-gold transition-all duration-300" />
            </a>
          </div>
        </div>

        {/* Decorative rule */}
        <div className="mt-10 flex items-center gap-4">
          <div className="h-px flex-1 bg-primary-foreground/8" />
          <span className="w-1.5 h-1.5 rounded-full bg-gold/40" />
          <div className="h-px flex-1 bg-primary-foreground/8" />
        </div>
      </motion.div>

      {/* ── Grid ── */}
      {loading && (
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-1">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonTile key={i} />)}
        </div>
      )}

      {error && !loading && (
        <div className="max-w-6xl mx-auto text-center py-16">
          <p className="font-body text-xs tracking-[0.2em] uppercase text-primary-foreground/65 mb-4">
            Could not load posts
          </p>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-body text-xs tracking-[0.2em] uppercase text-gold hover:text-gold transition-colors duration-300"
          >
            Visit us on Instagram →
          </a>
        </div>
      )}

      {!loading && !error && posts.length > 0 && (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-1"
        >
          {posts.map((post) => (
            <PostTile key={post.id} post={post} />
          ))}
        </motion.div>
      )}

      {/* Mobile CTA */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ duration: 0.65, delay: 0.5 }}
        className="max-w-6xl mx-auto mt-10 flex justify-center md:hidden"
      >
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-3 border border-primary-foreground/20 hover:border-gold px-8 py-3.5 transition-all duration-400"
        >
          <span className="font-body text-[10px] tracking-[0.3em] uppercase text-primary-foreground/60 group-hover:text-gold transition-colors duration-300">
            Follow on Instagram
          </span>
        </a>
      </motion.div>
    </section>
  );
};

export default InstagramSection;
